import { IxChain } from "@shared/src/config/chains";
import { Chain, createWalletClient, http, sha256 } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { WalletClientSigner } from "@aa-sdk/core";
import { wagmiConfig } from "@shared/src/config/wagmiConfig";

// Secp256k1 curve order (n) as a hex string
const CURVE_ORDER = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141");

/**
 * Generates a deterministic private key from a user's Ethereum address.
 * @param {string} address - The user's Ethereum address (e.g., "0x1234...abcd").
 * @returns {string} - A 64-character hexadecimal private key (with 0x prefix).
 */
export function generateDeterministicPrivateKey(address: `0x${string}`): `0x${string}` {
  // Ensure address is a string and lowercase for consistency
  if (typeof address !== "string") {
    throw new Error("Address must be a string");
  }
  const normalizedAddress = address.toLowerCase() as `0x${string}`;

  // Generate SHA-256 hash of the address (returns a hex string with 0x prefix)
  const hash = sha256(normalizedAddress);

  // Convert hash to BigInt (remove 0x prefix for conversion)
  let privateKeyInt = BigInt(hash);

  // Adjust if out of valid range (must be 1 <= key < curve order)
  if (privateKeyInt === BigInt(0) || privateKeyInt >= CURVE_ORDER) {
    privateKeyInt = (privateKeyInt % (CURVE_ORDER - BigInt(1))) + BigInt(1);
  }

  // Convert to 64-character hex string (pad with zeros if needed)
  const privateKeyHex = privateKeyInt.toString(16).padStart(64, "0");

  // Return with 0x prefix (standard for Ethereum private keys)
  return ("0x" + privateKeyHex) as `0x${string}`;
}

export function getUnsafeSigner(chain: Chain, address: `0x${string}`) {
  const privateKey = generateDeterministicPrivateKey(address);
  const account = privateKeyToAccount(privateKey);
  console.log("unsafeaccount", { account });

  // Set up a wallet client (e.g., for mainnet)
  const client = createWalletClient({
    account,
    chain: chain,
    transport: wagmiConfig._internal.transports[chain.id], // Replace with your node URL
  });
  const signer = new WalletClientSigner(client, "wallet");
  return signer;
}
